import { Location } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit, Renderer2, ViewChild, DOCUMENT, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { fromEvent, Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { BeezzService } from './services/beezz/beezz.service';
import { MenuService } from './services/menu/menu.service';
import { UtilService } from './services/util/util.service';
import { SocketService } from './services/socket/socket.service';
import { AuthService } from './services/auth/auth.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    imports: [RouterOutlet, ModalDirective]
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  util = inject(UtilService);
  bs = inject(BeezzService);
  menu = inject(MenuService);
  private meta = inject(Meta);
  private title = inject(Title);
  translate = inject(TranslateService);
  location = inject(Location);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private _renderer2 = inject(Renderer2);
  private socketService = inject(SocketService);
  private authService = inject(AuthService);
  document = inject<Document>(DOCUMENT);

  @ViewChild('smSlideUp') mdSlideUp: ModalDirective;
  public onlineEvent: Observable<Event>;
  public offlineEvent: Observable<Event>;
  public subscriptions: Subscription[] = [];
  public connectionStatusMessage: string;
  public connectionStatus: string;
  loaded: boolean = false;
  resizeObservable$: Observable<Event>
  resizeSubscription$: Subscription
  actualWidth: any;
  private selectedPodType
  private selectedPodId

  public constructor() {
    this.util.document = this.document;
    if (localStorage.getItem('dark-mode')) {
      this.util.document.body.classList.add(localStorage.getItem('dark-mode'));
    }
  }

  ngOnInit() {
    this.util.getServerConfiguration().subscribe(data => {
      this.authService.getSSOConfig();
      this.document.getElementById('appFavicon').setAttribute('href', data.externalFavIcon);
      this.meta.addTag({ name: 'apple-itunes-app', content: 'app-id=' + data.appIOSUrl });
      this.meta.addTag({ name: 'og:title', content: data.title });
      this.meta.addTag({ name: 'description', content: data.splashScreen });
      this.meta.addTag({ name: 'og:description', content: data.splashScreen });
      this.meta.addTag({ name: 'og:image', content: data.thumbLoginBackground });
      // Carrega config do módulo Beezz (Lambda) — alimenta `bs.beezzConfiguration`
      // consumido por `bs.isFollowFeatureEnabled()`. Best-effort: erro deixa
      // o flag como null → `isFollowFeatureEnabled()` retorna false (UI esconde follow).
      this.bs.loadBeezzConfiguration().subscribe({ error: () => {} });
    });
    if (this.util.getStoredConfiguration()?.bodyFontColorWhite) {
      this._renderer2.addClass(document.body, 'bodyFontColorWhite');
    }

    if (this.util.getStoredConfiguration()?.crisisModeBackgroundColor) {
      this._renderer2.setStyle(document.body, 'background-color', this.util.getStoredConfiguration()?.crisisModeBackgroundColor);
    }

    let appName = this.util.getStoredConfiguration()?.title;
    this.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe((event) => {
      this.title.setTitle(appName);
      this.menu.appendToTitle();
      this.util.getNavParams(this.route).subscribe((data: any) => {
        if (data && data.title) {
          this.title.setTitle(appName + ' - ' + data.title);
          this.menu.appendToTitle();
        } else {
          this.getPod(this.router.url.substring(1)).then((pod: any) => {
            if (pod && pod.title) {
              this.title.setTitle(appName + ' - ' + this.translate.instant(pod.title));
            } else {
              this.title.setTitle(appName);
            }
            this.menu.appendToTitle();
            this.menu.setSelectedPod(pod);
          })
        }
      });

      if (document.body.querySelector('.ng-sidebar__content')) {
        document.body.querySelector('.ng-sidebar__content').scrollTo({
          top: 0,
          behavior: 'auto',
        });
      }

      if (window.innerWidth >= 1680 && this.actualWidth != window.innerWidth) {
        this.menu.expandSidebar();
      } else if (window.innerWidth < 1680 && this.actualWidth != window.innerWidth) {
        this.menu.retractSidebar();
      }
      this.actualWidth = window.innerWidth;

      this.resizeObservable$ = fromEvent(window, 'resize')
      this.resizeSubscription$ = this.resizeObservable$.subscribe(evt => {
        if (window.innerWidth >= 1680 && window.innerWidth > this.actualWidth) {
          this.menu.expandSidebar();
        } else if (window.innerWidth < 1680 && window.innerWidth < this.actualWidth) {
          this.menu.retractSidebar();
        }
        this.actualWidth = window.innerWidth;
      })
    });
    let defaultLang = this.menu.getUserDefaultLang();
    if (defaultLang) {
      this.translate.setFallbackLang(defaultLang);
      this.translate.use(defaultLang);
    } else {
      this.translate.setFallbackLang(this.util.translate.getBrowserLang());
      this.translate.use(this.util.translate.getBrowserLang());
    }

    this.onlineEvent = fromEvent(window, 'online');
    this.offlineEvent = fromEvent(window, 'offline');

    this.subscriptions.push(this.onlineEvent.subscribe(event => {
      this.mdSlideUp.hide();
      if (this.router.url.includes('videowall/')) {
        window.location.reload();
      }
    }));
    this.subscriptions.push(this.offlineEvent.subscribe(e => {
      this.mdSlideUp.show();
    }));

    this.subscriptions.push(this.socketService.inactivateSubject.subscribe(data => {
      this.authService.logout();
    }));

    if (this.util.configuration?.showRecentActivities) {
      this.appendVlibras();
    }
  }

  appendVlibras() {
    let script = this._renderer2.createElement('script');
    script.text = `
             new window.VLibras.Widget('https://vlibras.gov.br/app');
        `;

    this._renderer2.appendChild(this.document.body, script);
  }

  private datepickerObserver: MutationObserver;

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.loaded = true;
    }, 2000);
    this.initDatepickerPositionFix();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.datepickerObserver?.disconnect();
  }

  private initDatepickerPositionFix(): void {
    this.datepickerObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of Array.from(mutation.addedNodes)) {
          if (node instanceof HTMLElement &&
              (node.tagName === 'BS-DATERANGEPICKER-CONTAINER' || node.tagName === 'BS-DATEPICKER-CONTAINER')) {
            this.fixDatepickerPosition(node);
          }
        }
      }
    });
    this.datepickerObserver.observe(document.body, { childList: true });
  }

  private fixDatepickerPosition(container: HTMLElement): void {
    setTimeout(() => {
      const trigger = document.activeElement as HTMLElement;
      if (trigger) {
        const rect = trigger.getBoundingClientRect();
        container.style.position = 'fixed';
        container.style.top = `${rect.bottom + 2}px`;
        container.style.left = `${rect.left}px`;
      }
    }, 50);
  }

  getPod(podType) {
    return new Promise(resolve => {
      if (podType.indexOf("customLink") !== -1) {
        this.selectedPodId = podType.split("customLink/")[1];
        this.selectedPodType = "customLink";
      } else if (podType.indexOf("urlIntegration") !== -1) {
        this.selectedPodId = podType.split("urlIntegration/")[1];
        this.selectedPodType = "urlIntegration";
      } else if (podType.indexOf("wiki/") !== -1) {
        let suffix = podType.split("wiki/")[1]
        this.selectedPodId = suffix.split("/detail")[0];
        this.selectedPodType = "wikiDetail";
      }
      const menuStorage = JSON.parse(localStorage.getItem('menu'));
      if (menuStorage) {
        for (const podCategory of menuStorage) {
          for (const pod of podCategory.podTOs) {
            if (this.selectedPodType === "customLink" || this.selectedPodType === "urlIntegration") {
              if (pod.id == this.selectedPodId) {
                resolve(pod);
                return;
              }
            } else if (this.selectedPodType === "wikiDetail") {
              if (pod.wikiId == this.selectedPodId) {
                resolve(pod);
                return;
              }
            } else {
              if (pod.podType.toUpperCase() === podType.toUpperCase()) {
                resolve(pod);
                return;
              }
            }
          }
        }
      }
      resolve(null);
      return;
    });
  }
}
